import React, { useState, useEffect } from 'react';
import './Contact.css';
import emailjs from '@emailjs/browser';

// Modern brand icons (inline SVG)
const SocialIcon = ({ name }) => {
  const n = (name || '').toLowerCase();
  switch (n) {
    case 'linkedin':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="currentColor">
          <path d="M4.98 3.5C4.98 4.88 3.86 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1 4.98 2.12 4.98 3.5zM0 24h5V7.98H0V24zM8.12 7.98h4.78v2.17h.07c.66-1.25 2.28-2.57 4.69-2.57 5.02 0 5.95 3.3 5.95 7.58V24h-4.99v-6.79c0-1.62-.03-3.7-2.26-3.7-2.26 0-2.61 1.77-2.61 3.58V24H8.12V7.98z" />
        </svg>
      );
    case 'facebook':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="currentColor">
          <path d="M22.675 0h-21.35C.596 0 0 .596 0 1.333v21.334C0 23.404.596 24 1.325 24h11.495v-9.294H9.691V11.01h3.129V8.414c0-3.1 1.894-4.788 4.66-4.788 1.325 0 2.463.099 2.795.143v3.24l-1.918.001c-1.504 0-1.796.716-1.796 1.766v2.315h3.587l-.467 3.696h-3.12V24h6.116C23.404 24 24 23.404 24 22.667V1.333C24 .596 23.404 0 22.675 0z" />
        </svg>
      );
    case 'instagram':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="currentColor">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.342 3.608 1.317.975.975 1.255 2.242 1.317 3.608.058 1.266.07 1.646.07 4.842s-.012 3.576-.07 4.842c-.062 1.366-.342 2.633-1.317 3.608-.975.975-2.242 1.255-3.608 1.317-1.266.058-1.646.07-4.85.07s-3.584-.012-4.85-.07c-1.366-.062-2.633-.342-3.608-1.317-.975-.975-1.255-2.242-1.317-3.608C2.175 15.76 2.163 15.38 2.163 12s.012-3.576.07-4.842c.062-1.366.342-2.633 1.317-3.608.975-.975 2.242-1.255 3.608-1.317C8.416 2.175 8.796 2.163 12 2.163zm0-2.163C8.741 0 8.332.013 7.052.072 5.771.131 4.725.403 3.8 1.328c-.925.925-1.197 1.971-1.256 3.252C2.485 5.861 2.472 6.269 2.472 9.528v4.944c0 3.259.013 3.667.072 4.948.059 1.281.331 2.327 1.256 3.252.925.925 1.971 1.197 3.252 1.256 1.281.059 1.689.072 4.948.072s3.667-.013 4.948-.072c1.281-.059 2.327-.331 3.252-1.256.925-.925 1.197-1.971 1.256-3.252.059-1.281.072-1.689.072-4.948V9.528c0-3.259-.013-3.667-.072-4.948-.059-1.281-.331-2.327-1.256-3.252-.925-.925-1.971-1.197-3.252-1.256C15.667.013 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zm0 10.162a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.88 1.44 1.44 0 0 0 0-2.88z" />
        </svg>
      );
    case 'twitter':
    case 'x':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 4l16 16M20 4L4 20" />
        </svg>
      );
    default:
      return null;
  }
};

const Contact = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [errorDetails, setErrorDetails] = useState('');

  // Initialize EmailJS with public key if configured
  useEffect(() => {
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
    if (publicKey) {
      try {
        emailjs.init(publicKey);
      } catch (err) {
        console.error('EmailJS init error:', err);
      }
    }
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('contact');
    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorDetails('');

    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    if (!serviceId || !templateId || !publicKey) {
      setIsSubmitting(false);
      setSubmitStatus('config_error');
      setErrorDetails('Missing EmailJS configuration. Please ensure Service ID, Template ID, and Public Key are set.');
      return;
    }

    try {
      await emailjs.send(serviceId, templateId, {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        company: formData.company,
        subject: formData.subject,
        message: formData.message,
        page_url: window.location.href,
        // Helpful extras
        reply_to: formData.email,
        to_email: 'oxgensurgicalsinfo@gmail.com',
        to_name: 'Oxgen Surgicals'
      }, publicKey);

      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('EmailJS send error:', error);
      setSubmitStatus('error');
      const detail = (error && (error.text || error.message)) || 'Unknown error occurred while sending the email.';
      setErrorDetails(detail);
    } finally {
      setIsSubmitting(false);
      if (submitStatus !== 'config_error') {
        setTimeout(() => setSubmitStatus(''), 5000);
      }
    }
  };

  const contactInfo = [
    {
      icon: '⏰',
      title: 'Business Hours',
      details: ['Available 24/7'],
      variant: 'business-hours'
    },
    {
      icon: '📞',
      title: 'WhatsApp',
      details: ['+92 3251422010'],
      variant: 'gradient'
    },
    {
      icon: '✉️',
      title: 'Email Us',
      details: ['oxgensurgicalsinfo@gmail.com', 'sales@oxgensurgicals.com'],
      variant: 'gradient'
    },
    {
      icon: '🌐',
      title: 'Follow Us',
      details: [
        { name: 'LinkedIn', icon: 'linkedin', href: 'https://www.linkedin.com/company/oxgen-surgicals/' },
        { name: 'Facebook', icon: 'facebook', href: 'https://www.facebook.com/profile.php?id=61581554500225' },
        { name: 'Instagram', icon: 'instagram', href: 'https://www.instagram.com/oxgensurgicals/' },
        { name: 'Twitter', icon: 'x', href: 'https://twitter.com/oxgensurgicals' }
      ],
      variant: 'gradient'
    }
  ];

  return (
    <section id="contact" className="contact section">
      <div className="container">
        <div className={`section-header ${isVisible ? 'animate-in' : ''}`}>
          <h2 className="section-title">Get In Touch</h2>
          <p className="section-description">
            Ready to discuss your surgical instrument needs? Contact our expert team for personalized solutions.
            For sales, email us at <a href="mailto:sales@oxgensurgicals.com">sales@oxgensurgicals.com</a>.
          </p>
        </div>

        <div className="contact-content">
          <div className={`contact-info ${isVisible ? 'animate-in' : ''}`}>
            <div className="info-header">
              <h3>Contact Information</h3>
              <p>We're here to help you find the perfect surgical instruments for your medical practice.</p>
            </div>
            
            <div className="info-grid">
              {contactInfo.map((info, index) => (
                <div 
                  key={index} 
                  className={`info-card ${info.variant ? info.variant : ''}`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="info-icon">{info.icon}</div>
                  <div className="info-content">
                    <h4>{info.title}</h4>
                    {info.title === 'Follow Us' ? (
                      <div className="social-links">
                        {info.details.map((social, idx) => (
                          <a
                            key={idx}
                            href={social.href}
                            className="social-link"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={social.name}
                            title={social.name}
                          >
                            <span className="social-icon"><SocialIcon name={social.icon} /></span>
                          </a>
                        ))}
                      </div>
                    ) : (
                      info.details.map((detail, idx) => (
                        <p key={idx}>
                          {info.title === 'Email Us' || /\S+@\S+\.\S+/.test(detail) ? (
                            <a href={`mailto:${detail}`} className="email-link">{detail}</a>
                          ) : info.title === 'Call Us' || /^\+?\d[\d\s-]+$/.test(detail) ? (
                            <>
                              <a href={`tel:${detail.replace(/\s+/g, '')}`} className="phone-link">{detail}</a>
                              <a href={`https://wa.me/${detail.replace(/\D/g, '')}`} className="whatsapp-link" target="_blank" rel="noopener noreferrer" aria-label={`WhatsApp ${detail}`}>WhatsApp</a>
                            </>
                          ) : detail}
                        </p>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="office-map">
               <h4>Visit Our Office</h4>
               <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                 <iframe
                   title="Office Location Map"
                   src="https://maps.google.com/maps?q=32.484973,74.530276&z=14&output=embed"
                   style={{ width: '100%', height: 350, border: 0 }}
                   allowFullScreen=""
                   loading="lazy"
                   referrerPolicy="no-referrer-when-downgrade"
                 ></iframe>
               </div>
               <a
                 className="open-maps-btn"
                 href="https://maps.app.goo.gl/SHjAKdphKLmHGcxD8"
                 target="_blank"
                 rel="noopener noreferrer"
                 aria-label="Open in Google Maps"
               >
                 Open in Google Maps
               </a>
             </div>
          </div>

          <div className={`contact-form-container ${isVisible ? 'animate-in' : ''}`}>
            <div className="form-header">
              <h3>Send Us a Message</h3>
              <p>Fill out the form below and we'll get back to you within 24 hours.</p>
            </div>

            {submitStatus === 'success' && (
              <div className="success-message">
                <div className="success-icon">✅</div>
                <h4>Message Sent Successfully!</h4>
                <p>Thank you for contacting us. We'll get back to you soon.</p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="error-message" role="alert">
                <div className="error-icon">❌</div>
                <h4>Message Failed to Send</h4>
                <p>Please try again later or contact us directly at <a href="mailto:oxgensurgicalsinfo@gmail.com">oxgensurgicalsinfo@gmail.com</a> or <a href="mailto:sales@oxgensurgicals.com">sales@oxgensurgicals.com</a>.</p>
                {errorDetails && (
                  <p className="error-details">Error details: {errorDetails}</p>
                )}
              </div>
            )}

            {submitStatus === 'config_error' && (
              <div className="error-message" role="alert">
                <div className="error-icon">⚙️</div>
                <h4>Email Service Not Configured</h4>
                <p>The email service is not configured. Please set REACT_APP_EMAILJS_SERVICE_ID, REACT_APP_EMAILJS_TEMPLATE_ID, and REACT_APP_EMAILJS_PUBLIC_KEY.</p>
              </div>
            )}

            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your full name"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your email"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter your phone number"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="company">Company/Organization</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    placeholder="Enter your company name"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="subject">Subject *</label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select a subject</option>
                  <option value="product-inquiry">Product Inquiry</option>
                  <option value="quote-request">Quote Request</option>
                  <option value="technical-support">Technical Support</option>
                  <option value="partnership">Partnership Opportunity</option>
                  <option value="general">General Question</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows="6"
                  placeholder="Tell us about your requirements or questions..."
                ></textarea>
              </div>

              <button 
                type="submit" 
                className={`btn btn-primary btn-large submit-btn ${isSubmitting ? 'submitting' : ''}`}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner"></span>
                    Sending Message...
                  </>
                ) : (
                  'Send Message'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;